<template>
  <FormCarousel ref="carouselControls">
    <!-- Field 1: first name -->
    <FormCarouselItem
      name="firstName"
      :label="t('onboarding.details.first_name_label')"
      :disable-next-if="v$.firstName.$invalid"
      @focus="firstNameFieldRef?.focus"
      @input="clearError"
    >
      <FormTextInput
        ref="firstNameFieldRef"
        v-model="form.firstName"
        autocapitalize="words"
        autofocus
        :error-messages="validationErrors(v$.firstName.$errors)"
      />
    </FormCarouselItem>

    <!-- Field 2: last name -->
    <FormCarouselItem
      name="lastName"
      :label="t('onboarding.details.last_name_label')"
      :disable-next-if="v$.lastName.$invalid"
      @focus="lastNameFieldRef?.focus"
      @input="clearError"
    >
      <FormTextInput
        ref="lastNameFieldRef"
        v-model="form.lastName"
        autocapitalize="words"
        :error-messages="validationErrors(v$.lastName.$errors)"
      />
    </FormCarouselItem>

    <!-- Field 3: phone number -->
    <FormCarouselItem
      name="phoneNumber"
      :disable-next-if="v$.phoneNumber.$invalid"
      :label="t('onboarding.details.phone_number_label')"
      @focus="phoneNumberFieldRef?.focus"
      @input="clearError"
    >
      <y-phone-number ref="phoneNumberFieldRef" v-model="form.phoneNumber" />
    </FormCarouselItem>

    <!-- Field 4: country -->
    <FormCarouselItem
      name="country"
      :disable-next-if="v$.country.$invalid"
      :label="t('onboarding.details.country_label')"
      :enter-submit="false"
      @focus="() => countryFieldRef?.focus({ preventScroll: true })"
      @input="clearError"
    >
      <y-autocomplete
        ref="countryFieldRef"
        v-model="form.country"
        input-class="form-disable-enter"
        :items="tradedCountries"
        item-title="name"
        item-value="code"
        :placeholder="t('form.address.country')"
      />
    </FormCarouselItem>

    <!-- Field 5: entity -->
    <FormCarouselItem
      name="entity"
      :label="t('onboarding.details.entity_type_label')"
      no-next-button
      @input="clearError"
    >
      <v-item-group
        v-model="form.entity"
        mandatory
        :class="`d-flex ga-4 me-4 ${xs ? 'flex-column' : ''}`"
      >
        <v-item v-slot="{ isSelected, toggle }" value="company">
          <button
            v-ripple="{ class: 'bg-purple-4' }"
            :class="`form-disable-enter pa-5 rounded-xl elevation-1 d-flex justify-space-between cursor-pointer ${isSelected ? 'bg-pink-4' : 'bg-background'}`"
            :style="`flex: 1 0 ${smAndUp ? '50%' : '100%'}`"
            @click="
              toggle && toggle();
              goToNextSlide();
            "
          >
            <span>{{ t("onboarding.details.limited_company_option") }}</span>
            <v-icon>mdi-arrow-right</v-icon>
          </button>
        </v-item>
        <v-item v-slot="{ isSelected, toggle }" value="sole-trader">
          <button
            v-ripple="{ class: 'bg-purple-4' }"
            :class="`form-disable-enter pa-5 rounded-xl elevation-1 d-flex justify-space-between cursor-pointer ${isSelected ? 'bg-pink-4' : 'bg-background'}`"
            :style="`flex: 1 0 ${smAndUp ? '50%' : '100%'}`"
            @click="
              toggle && toggle();
              goToNextSlide();
            "
          >
            <span>{{ t("onboarding.details.sole_trader_option") }}</span>
            <v-icon>mdi-arrow-right</v-icon>
          </button>
        </v-item>
      </v-item-group>
    </FormCarouselItem>

    <!-- Field 6: legal entity name -->
    <FormCarouselItem
      name="legalEntityName"
      :disable-next-if="v$.legalEntityName.$invalid"
      :label="
        form.entity === 'company'
          ? t('onboarding.details.company_name_label')
          : t('onboarding.details.trading_name_label')
      "
      @focus="legalEntityNameFieldRef?.focus"
      @input="clearError"
    >
      <CompanySearch
        ref="legalEntityNameFieldRef"
        :country="form.country || 'GB'"
        :error-messages="validationErrors(v$.legalEntityName.$errors)"
        @result="onCompanyResult"
      />
    </FormCarouselItem>

    <!-- Field 7: company registration number -->
    <FormCarouselItem
      name="registrationNumber"
      :disabled="form.entity === 'sole-trader'"
      :disable-next-if="v$.registrationNumber.$invalid"
      :label="t('onboarding.details.registration_number_label')"
      @focus="registrationNumberFieldRef?.focus"
      @input="clearError"
    >
      <FormTextInput
        ref="registrationNumberFieldRef"
        v-model="form.registrationNumber"
        label="Company number"
        autocapitalize="words"
        :hint="t(`onboarding.details.registration_number_hint_${form.country?.toLowerCase()}`, '')"
        :error-messages="validationErrors(v$.registrationNumber.$errors)"
      />
    </FormCarouselItem>

    <!-- Field 8: industry code -->
    <FormCarouselItem
      name="industryCode"
      :disable-next-if="v$.industryCode.$invalid"
      :label="t('onboarding.details.industry_label')"
      :next-loading="submitting"
      :enter-submit="false"
      @focus="() => industryCodeFieldRef?.focus({ preventScroll: true })"
      @next="submit"
    >
      <y-autocomplete
        ref="industryCodeFieldRef"
        v-model="form.industryCode"
        input-class="form-disable-enter"
        :items="industryCodes"
        item-title="name"
        item-value="value"
        :label="t('onboarding.details.industry_field_label')"
        @update:model-value="clearError"
      />
    </FormCarouselItem>
  </FormCarousel>
</template>

<script setup lang="ts">
import {
  DraftMerchantEntityEnum,
  Industry,
  IndustryStatusEnum,
  UpdateDraftMerchantDetails,
} from "@tableyeti/merchant-service";
import useVuelidate, { ErrorObject } from "@vuelidate/core";
import { required, helpers } from "@vuelidate/validators";
import { parsePhoneNumber } from "awesome-phonenumber";
import { computed, onMounted, reactive, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useDisplay } from "vuetify";
import { VAutocomplete } from "vuetify/components";
import CompanySearch from "../Companies/CompanySearch.vue";
import { FormCarousel } from "../Form";
import FormTextInput from "@/components/Form/FormTextInput.vue";
import YPhoneNumber from "@/components/YPhoneNumber.vue";
import { OnboardingError, useOnboardingStore } from "@/store/onboarding";
import { Company } from "@/utils/company";
import { tradedCountries } from "@/utils/config";
import { i18nRequired, isRegistrationNumber } from "@/utils/custom-validators";

const { t } = useI18n();
const { xs, smAndUp } = useDisplay();
const carouselControls = ref<InstanceType<typeof FormCarousel>>();

const goToNextSlide = () => {
  carouselControls.value?.next();
};
const onboardingStore = useOnboardingStore();

const firstNameFieldRef = ref<typeof FormTextInput>();
const lastNameFieldRef = ref<typeof FormTextInput>();
const phoneNumberFieldRef = ref<typeof YPhoneNumber>();
const countryFieldRef = ref<typeof VAutocomplete>();
const legalEntityNameFieldRef = ref<typeof CompanySearch>();
const registrationNumberFieldRef = ref<typeof FormTextInput>();
const industryCodeFieldRef = ref<typeof VAutocomplete>();

const industryCodes = ref<Industry[]>([]);

onMounted(async () => {
  industryCodes.value = await onboardingStore.getIndustries();
});

const form = reactive({
  firstName: "",
  lastName: "",
  phoneNumber: "",
  country: null as null | string,
  entity: null as null | DraftMerchantEntityEnum,
  legalEntityName: "",
  registrationNumber: "",
  industryCode: null as null | string,
});

const industryNameFromCode = (code: string | null) => {
  return industryCodes.value.find((industry) => industry.value === code)?.name;
};

const rules = computed(() => ({
  firstName: {
    required: i18nRequired,
    $autoDirty: true, //required to show error message
  },
  lastName: {
    required: i18nRequired,
    $autoDirty: true, //required to show error message
  },
  phoneNumber: {
    required: i18nRequired,
    phoneNumberValidator: helpers.withMessage(
      t("form.phoneNumberInvalid"),
      () => parsePhoneNumber(form.phoneNumber).valid,
    ),
  },
  country: { i18nRequired },
  entity: { required },
  legalEntityName: {
    required: i18nRequired,
    $autoDirty: true, //required to show error message
  },
  registrationNumber:
    form.entity === "sole-trader"
      ? {}
      : {
          required: i18nRequired,
          $autoDirty: true, //required to show error message
          regexp: helpers.withMessage(t("onboarding.details.registration_number_invalid"), () =>
            isRegistrationNumber(form.country ?? "", form.registrationNumber),
          ),
        },
  industryCode: { required: i18nRequired },
}));

// TODO: don't using auto dirty, touch on invalid next button click / enter press
const v$ = useVuelidate(rules, form);

const submitting = ref<boolean>(false);

const submit = async () => {
  carouselControls.value?.clearError();
  v$.value.$touch();
  if (v$.value.$invalid) {
    carouselControls.value?.setError(
      v$.value.$errors[0]?.$message.toString() || t("form.error_invalid_fields"),
    );
    return;
  }

  const merchantRequest = {
    ...form,
    email: onboardingStore.draftMerchant!.email,
  } as UpdateDraftMerchantDetails;

  if (merchantRequest.entity === DraftMerchantEntityEnum.SoleTrader) {
    delete merchantRequest.registrationNumber;
  }

  try {
    submitting.value = true;
    await onboardingStore.updateDraftMerchant(merchantRequest);
    await onboardingStore.submitDraftMerchant();
  } catch (error) {
    if (error instanceof OnboardingError) {
      // Handle industry code not approved error
      if (error.message === "industry code not approved") {
        carouselControls.value?.goToItemByName("industryCode");
        carouselControls.value?.setError(
          t("form.notApprovedIndustryError", { industry: industryNameFromCode(form.industryCode) }),
          "industryCode",
        );
        return;
      }
      carouselControls.value?.setError(error.message, error.field ?? "firstName");
    } else {
      carouselControls.value?.setError(t("form.error_something_went_wrong"), "firstName");
    }
  } finally {
    submitting.value = false;
  }
};

const validationErrors = (errors: ErrorObject[]) => {
  return errors.map((e) => e.$message?.toString());
};

const onCompanyResult = (company: Partial<Company>) => {
  form.legalEntityName = company.companyName || "";
  form.registrationNumber = company.companyNumber || "";
  if (company.sicCodes && company.sicCodes.length > 0) {
    for (const adyenCode of industryCodes.value) {
      for (const countrySicCode of adyenCode.mapping?.GB ?? []) {
        if (
          countrySicCode.code &&
          company.sicCodes.includes(countrySicCode.code) &&
          adyenCode.status === IndustryStatusEnum.Approved
        ) {
          form.industryCode = adyenCode.value;
          return;
        }
      }
    }
  }
};

const clearError = () => carouselControls.value?.setError("");
</script>

<style scoped>
button:focus {
  outline: 1px solid rgba(var(--v-theme-purple-4), 0.5);
}
</style>
